import { colors } from 'src/app/colors';

export interface StatCard {
    icon: string;
    label: string;
    value: string;
    subtitle?: string;
    iconColor?: string;
    iconBgColor?: string;
}


export const reportsStatsConfig: StatCard[] = [
    {
        icon: 'timelapse',
        label: 'Total Active Time',
        value: '00h 00m',
        iconColor: 'text-blue-500',
        iconBgColor: 'bg-blue-100'
    },
    {
        icon: 'work_history',
        label: 'Shift Time',
        value: '00h 00m',
        iconColor: 'text-indigo-500',
        iconBgColor: 'bg-indigo-100'
    },
    {
        icon: 'bolt',
        label: 'Overtime',
        value: '00h 00m',
        iconColor: 'text-orange-500',
        iconBgColor: 'bg-orange-100'
    },
    {
        icon: 'desktop_mac',
        label: 'Top App',
        value: 'N/A',
        subtitle: '0 apps tracked',
        iconColor: 'text-purple-500',
        iconBgColor: 'bg-purple-100'
    },
    {
        icon: 'public',
        label: 'Top Website',
        value: 'N/A',
        subtitle: '0 sites tracked',
        iconColor: 'text-blue-600',
        iconBgColor: 'bg-blue-100'
    }
];

export const createChartConfig = (type: string) => {
    return {
        type: 'bar',
        data: {
            labels: [],
            datasets: [
                {
                    label: type == 'all' ? 'Websites & Apps Usage' : type == 'web' ? 'Websites Usage' : 'Application Usage',
                    data: [],
                    backgroundColor: colors.BarColor,
                    barThickness: 24,
                }
            ]
        },
        options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                    position: 'bottom',
                    labels: {
                        font: { size: 12 },
                        padding: 15,
                        usePointStyle: true
                    }
                },
                tooltip: {
                    enabled: false,
                    mode: 'nearest',
                    intersect: true,
                    external: topUsageChartTooltip()
                },
            },
            scales: {
                x: {
                    beginAtZero: true,
                    ticks: {
                        font: { size: 11 },
                        callback: function (value: any) {
                            return value + 'h';
                        }
                    },
                    grid: {
                        color: colors.topUsageChart.gridBorderColor,
                        drawBorder: false
                    }
                },
                y: {
                    ticks: {
                        font: { size: 11 }
                    },
                    grid: {
                        display: false,
                        drawBorder: false
                    }
                }
            }
        }
    }
}


export const createDailyUsageChartConfig = (labels: string[], appData: number[], webData: number[], appGradient: any, webGradient: any, yAxisMax: number) => {
    return {
        type: 'line',
        data: {
            labels: labels,
            datasets: [
                {
                    label: 'Apps',
                    data: appData,
                    borderColor: colors.dailyUsageChart.appColor,
                    backgroundColor: appGradient,
                    borderWidth: 2,
                    fill: true,
                    tension: 0.4,
                    pointRadius: 3,
                    pointBackgroundColor: colors.dailyUsageChart.appColor,
                    pointBorderColor: 'transparent',
                    pointBorderWidth: 0,
                    pointHoverRadius: 3,
                    pointHoverBackgroundColor: colors.dailyUsageChart.appColor,
                    pointHoverBorderColor: 'transparent',
                    pointHoverBorderWidth: 0,
                },
                {
                    label: 'Websites',
                    data: webData,
                    borderColor: colors.dailyUsageChart.webColor,
                    backgroundColor: webGradient,
                    borderWidth: 2,
                    fill: true,
                    tension: 0.4,
                    pointRadius: 3,
                    pointBackgroundColor: colors.dailyUsageChart.webColor,
                    pointBorderColor: 'transparent',
                    pointBorderWidth: 0,
                    pointHoverRadius: 3,
                    pointHoverBackgroundColor: colors.dailyUsageChart.webColor,
                    pointHoverBorderColor: 'transparent',
                    pointHoverBorderWidth: 0,
                }
            ]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: true, position: 'top', align: 'end', labels: { usePointStyle: true, boxWidth: 8 } },
                tooltip: {
                    enabled: false,
                    mode: 'index',
                    intersect: true,
                    position: 'nearest',
                    external: dailyUsageChartTooltip()
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    max: yAxisMax,
                    grid: { color: colors.dailyUsageChart.tooltip.borderColor, drawBorder: false },
                    ticks: {
                        font: { size: 11 },
                        stepSize: 2,
                        callback: (value: any) => {
                            return value + 'h';
                        }
                    }
                },
                x: { grid: { display: false } }
            }
        }
    }
}

const dailyUsageChartTooltip = () => {
    return function (context: any) {
        const { chart, tooltip: tooltipModel } = context;

        let tooltipEl = document.getElementById('chartjs-tooltip');

        if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip';
            tooltipEl.style.opacity = '0';
            tooltipEl.style.pointerEvents = 'none';
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.transition = 'all .1s ease';
            tooltipEl.style.padding = '12px';
            tooltipEl.style.borderRadius = '12px';
            tooltipEl.style.background = colors.dailyUsageChart.tooltip.backgroundColor;
            tooltipEl.style.boxShadow = `0 10px 15px -3px ${colors.dailyUsageChart.tooltip.shadowColorPrimary}, 0 4px 6px -2px ${colors.dailyUsageChart.tooltip.shadowColorSecondary}`;
            tooltipEl.style.border = `1px solid ${colors.dailyUsageChart.tooltip.borderColor}`;
            tooltipEl.style.zIndex = '1000';
            tooltipEl.style.minWidth = '190px';
            tooltipEl.style.backdropFilter = 'blur(4px)';

            const caret = document.createElement('div');
            caret.id = 'chartjs-tooltip-caret';
            caret.style.position = 'absolute';
            caret.style.width = '0';
            caret.style.height = '0';
            caret.style.borderLeft = '8px solid transparent';
            caret.style.borderRight = '8px solid transparent';
            caret.style.borderTop = `8px solid ${colors.dailyUsageChart.tooltip.borderColor}`;
            caret.style.bottom = '-8px';
            caret.style.left = '50%';
            caret.style.transform = 'translateX(-50%)';

            const caretInner = document.createElement('div');
            caretInner.style.position = 'absolute';
            caretInner.style.width = '0';
            caretInner.style.height = '0';
            caretInner.style.borderLeft = '7px solid transparent';
            caretInner.style.borderRight = '7px solid transparent';
            caretInner.style.borderTop = `7px solid ${colors.dailyUsageChart.tooltip.borderTopColor}`;
            caretInner.style.bottom = '1px';
            caretInner.style.left = '50%';
            caretInner.style.transform = 'translateX(-50%)';

            caret.appendChild(caretInner);
            tooltipEl.appendChild(caret);
            document.body.appendChild(tooltipEl);
        }

        if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
        }

        if (tooltipModel.body) {
            const titleLines = tooltipModel.title || [];

            const formatTime = (value: number) => {
                const totalMinutes = Math.round(value * 60);
                const h = Math.floor(totalMinutes / 60);
                const m = totalMinutes % 60;
                if (h > 0 && m > 0) return `${h}h ${m}m`;
                if (h > 0) return `${h}h`;
                return `${m}m`;
            };

            let innerHtml = `<div style="margin-bottom: 10px; border-bottom: 1px solid ${colors.dailyUsageChart.tooltip.tooltipBorderColor}; padding-bottom: 8px;">`;
            titleLines.forEach(function (title: string) {
                innerHtml += `<span style="font-weight: 700; font-size: 15px; color: ${colors.dailyUsageChart.tooltip.titleTextColor}; letter-spacing: -0.01em;">${title}</span>`;
            });
            innerHtml += '</div>';

            innerHtml += '<div style="display: flex; flex-direction: column; gap: 10px;">';
            let totalValue = 0;

            tooltipModel.dataPoints.forEach((dataPoint: any, i: number) => {
                const dataset = tooltipModel.labelColors[i];
                const label = dataPoint.datasetIndex === 0 ? 'Apps' : 'Websites';
                // v4: use parsed.y for line chart y-value
                const value = dataPoint.parsed?.y ?? dataPoint.raw ?? 0;
                totalValue += value;

                const color = dataset.borderColor;
                const timeStr = formatTime(value);

                innerHtml += `
            <div style="display: flex; align-items: center; justify-content: space-between;">
                <div style="display: flex; align-items: center; gap: 10px;">
                    <span style="display: block; width: 12px; height: 12px; border-radius: 4px; border: 1px solid ${label == 'Apps' ? colors.dailyUsageChart.appColor : colors.dailyUsageChart.webColor}; background: ${label == 'Apps' ? colors.dailyUsageChart.appGradient[0] : colors.dailyUsageChart.webGradient[0]}; box-shadow: 0 0 0 2px ${colors.dailyUsageChart.tooltip.markerInnerRingColor}, 0 0 0 3px ${color}${colors.dailyUsageChart.tooltip.markerOuterRingSuffix};"></span>
                    <span style="font-size: 13px; color: ${colors.dailyUsageChart.tooltip.rowLabelColor}; font-weight: 500;">${label}</span>
                </div>
                <span style="font-size: 13px; color: ${colors.dailyUsageChart.tooltip.rowValueColor}; font-weight: 700;">${timeStr}</span>
            </div>`;
            });
            innerHtml += '</div>';

            innerHtml += `<div style="margin: 12px 0; border-top: 1px dashed ${colors.dailyUsageChart.tooltip.borderColor};"></div>`;
            const totalTimeStr = formatTime(totalValue);
            innerHtml += `
            <div style="display: flex; flex-direction: column; gap: 6px;">
                <div style="display: flex; align-items: center; justify-content: space-between;">
                <span style="font-size: 12px; color: ${colors.dailyUsageChart.tooltip.totalLabelColor}; font-weight: 500;">Total Activity:</span>
                <span style="font-size: 12px; color: ${colors.dailyUsageChart.tooltip.totalValueColor}; font-weight: 800;">${totalTimeStr}</span>
                </div>
            </div>`;

            const caret = tooltipEl.querySelector('#chartjs-tooltip-caret');
            tooltipEl.innerHTML = innerHtml;
            if (caret) tooltipEl.appendChild(caret);
        }

        const position = chart.canvas.getBoundingClientRect();
        tooltipEl.style.opacity = '1';
        tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
        tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY - 14 + 'px';
        tooltipEl.style.transform = 'translate(-50%, -100%)';
    }
}

const topUsageChartTooltip = () => {
    return function (context: any) {
        const { chart, tooltip: tooltipModel } = context;

        let tooltipEl = document.getElementById('chartjs-tooltip');

        if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip';
            tooltipEl.style.opacity = '0';
            tooltipEl.style.pointerEvents = 'none';
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.transition = 'all .1s ease';
            tooltipEl.style.padding = '12px';
            tooltipEl.style.borderRadius = '12px';
            tooltipEl.style.background = colors.topUsageChart.tooltip.backgroundColor;
            tooltipEl.style.boxShadow = `0 10px 15px -3px ${colors.topUsageChart.tooltip.shadowColorPrimary}, 0 4px 6px -2px ${colors.topUsageChart.tooltip.shadowColorSecondary}`;
            tooltipEl.style.border = `1px solid ${colors.topUsageChart.tooltip.borderColor}`;
            tooltipEl.style.zIndex = '1000';
            tooltipEl.style.minWidth = '190px';
            tooltipEl.style.backdropFilter = 'blur(4px)';

            const caret = document.createElement('div');
            caret.id = 'chartjs-tooltip-caret';
            caret.style.position = 'absolute';
            caret.style.width = '0';
            caret.style.height = '0';
            caret.style.borderLeft = '8px solid transparent';
            caret.style.borderRight = '8px solid transparent';
            caret.style.borderTop = `8px solid ${colors.topUsageChart.tooltip.borderColor}`;
            caret.style.bottom = '-8px';
            caret.style.left = '50%';
            caret.style.transform = 'translateX(-50%)';

            const caretInner = document.createElement('div');
            caretInner.style.position = 'absolute';
            caretInner.style.width = '0';
            caretInner.style.height = '0';
            caretInner.style.borderLeft = '7px solid transparent';
            caretInner.style.borderRight = '7px solid transparent';
            caretInner.style.borderTop = `7px solid ${colors.topUsageChart.tooltip.borderTopColor}`;
            caretInner.style.bottom = '1px';
            caretInner.style.left = '50%';
            caretInner.style.transform = 'translateX(-50%)';

            caret.appendChild(caretInner);
            tooltipEl.appendChild(caret);
            document.body.appendChild(tooltipEl);
        }

        if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
        }

        if (tooltipModel.body) {
            const title = tooltipModel.title[0] || '';

            const formatTime = (value: number) => {
                const totalMinutes = Math.round(value * 60);
                const h = Math.floor(totalMinutes / 60);
                const m = totalMinutes % 60;
                if (h > 0 && m > 0) return `${h}h ${m}m`;
                if (h > 0) return `${h}h`;
                return `${m}m`;
            };

            let innerHtml = `<div style="margin-bottom: 10px; border-bottom: 1px solid ${colors.topUsageChart.tooltip.tooltipBorderColor}; padding-bottom: 8px;">`;
            innerHtml += `<span style="font-weight: 700; font-size: 15px; color: ${colors.topUsageChart.tooltip.titleTextColor}; letter-spacing: -0.01em; display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 220px;">${title}</span>`;
            innerHtml += '</div>';

            innerHtml += '<div style="display: flex; flex-direction: column; gap: 10px;">';

            tooltipModel.dataPoints.forEach((dataPoint: any, i: number) => {
                const dataset = tooltipModel.labelColors[i];
                const label = tooltipModel.body[i].lines[0].split(':')[0];
                // v4 horizontal bar (indexAxis: 'y'): value is on the x-axis → parsed.x
                const value = dataPoint.parsed?.x ?? dataPoint.raw ?? 0;

                const color = dataset.backgroundColor;
                const timeStr = formatTime(value);

                innerHtml += `
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center; gap: 10px;">
            <span style="display: block; width: 12px; height: 12px; border-radius: 4px; background: ${color}; box-shadow: 0 0 0 2px ${colors.topUsageChart.tooltip.markerInnerRingColor}, 0 0 0 3px ${color}${colors.topUsageChart.tooltip.markerOuterRingSuffix};"></span>
            <span style="font-size: 13px; color: ${colors.topUsageChart.tooltip.rowLabelColor}; font-weight: 500;">${label}</span>
          </div>
          <span style="font-size: 13px; color: ${colors.topUsageChart.tooltip.rowValueColor}; font-weight: 700; margin-left:5px;">${timeStr}</span>
        </div>`;
            });
            innerHtml += '</div>';

            const caret = tooltipEl.querySelector('#chartjs-tooltip-caret');
            tooltipEl.innerHTML = innerHtml;
            if (caret) tooltipEl.appendChild(caret);
        }

        const position = chart.canvas.getBoundingClientRect();
        tooltipEl.style.opacity = '1';
        tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
        tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY - 14 + 'px';
        tooltipEl.style.transform = 'translate(-50%, -100%)';
    }
}